/**
 * Push to DataLayer Utility
 * Core function for pushing data to window.dataLayer
 */

import { ANALYTICS_CONFIG } from './constants.js';

/**
 * Initialize dataLayer if not already present
 * Must be called BEFORE GTM loads
 */
export function initialize_data_layer() {
  if (typeof window === 'undefined') {
    return;
  }

  // Only initialize once
  if (window.dataLayer && Array.isArray(window.dataLayer)) {
    if (ANALYTICS_CONFIG.debug) {
      console.log('[Analytics] dataLayer already initialized');
    }
    return;
  }

  // Create new dataLayer
  window.dataLayer = [];

  if (ANALYTICS_CONFIG.debug) {
    console.log('[Analytics] dataLayer initialized');
  }
}

/**
 * Push data to dataLayer
 * @param {Object} payload - Data to push
 * @returns {boolean} - True if successfully pushed
 */
export function push_to_data_layer(payload) {
  // SSR-safe check
  if (typeof window === 'undefined') {
    return false;
  }

  // Ensure dataLayer exists
  if (!window.dataLayer || !Array.isArray(window.dataLayer)) {
    if (ANALYTICS_CONFIG.debug) {
      console.warn('[Analytics] dataLayer not initialized. Initializing now.');
    }
    initialize_data_layer();
  }

  // Validate payload
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    if (ANALYTICS_CONFIG.debug) {
      console.warn('[Analytics] Invalid payload for push:', payload);
    }
    return false;
  }

  try {
    // Add timestamp if not present
    if (!payload.timestamp) {
      payload.timestamp = Date.now();
    }

    // Push to dataLayer
    window.dataLayer.push(payload);

    if (ANALYTICS_CONFIG.debug) {
      console.log('[Analytics Event]', payload);
    }

    if (!payload.event) {
      console.warn('[Analytics] Pushed payload missing event property', payload);
    }

    if (payload.event === 'file_download' && (!payload.clickURL || !payload.pdf_name)) {
      console.warn('[Analytics] Pushed file_download payload missing clickURL or pdf_name', payload);
    }

    return true;
  } catch (error) {
    if (ANALYTICS_CONFIG.debug) {
      console.error('[Analytics] Error pushing to dataLayer:', error);
    }
    return false;
  }
}

/**
 * Get current dataLayer
 * @returns {Array} - Current dataLayer
 */
export function get_data_layer() {
  if (typeof window === 'undefined') {
    return [];
  }

  return window.dataLayer || [];
}

/**
 * Clear dataLayer (useful for testing)
 */
export function clear_data_layer() {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer = [];
  }
}

/**
 * Get dataLayer size
 * @returns {number} - Number of items in dataLayer
 */
export function get_data_layer_size() {
  if (typeof window === 'undefined') {
    return 0;
  }

  return (window.dataLayer || []).length;
}

/**
 * Get last N items from dataLayer
 * @param {number} count - Number of items to retrieve
 * @returns {Array} - Last N items
 */
export function get_data_layer_tail(count = 10) {
  if (typeof window === 'undefined') {
    return [];
  }

  const datalayer = window.dataLayer || [];
  return datalayer.slice(-count);
}

/**
 * Batch push multiple payloads
 * @param {Array<Object>} payloads - Array of payloads to push
 * @returns {number} - Number of successfully pushed payloads
 */
export function batch_push_to_data_layer(payloads) {
  if (!Array.isArray(payloads)) {
    return 0;
  }

  let success_count = 0;

  for (const payload of payloads) {
    if (push_to_data_layer(payload)) {
      success_count++;
    }
  }

  return success_count;
}
